import React, { useId, useState } from 'react';
import styles from './ExpandableCard.module.css';
import StatusTag from './StatusTag';
import { ChevronIcon } from './MarketingIcons';
import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * Headline-led card whose body is disclosed on tap.
 *
 * `alwaysOpenOnDesktop` is for sections that should scan as a grid of short
 * essays on a wide screen and as an accordion on a phone — the body is not
 * hidden, only presented differently.
 */
const ExpandableCard = ({
  title,
  summary,
  children,
  icon,
  status,
  defaultOpen = false,
  alwaysOpenOnDesktop = false,
  headingLevel = 'h3',
}) => {
  const panelId = useId();
  const buttonId = useId();
  const isDesktop = useMediaQuery('(min-width: 760px)');
  const [open, setOpen] = useState(defaultOpen);
  const pinnedOpen = alwaysOpenOnDesktop && isDesktop;
  const expanded = pinnedOpen || open;
  const Heading = headingLevel;

  if (pinnedOpen) {
    return (
      <article className={`${styles.card} ${styles.cardOpen} ${styles.cardDesktopOpen}`}>
        <Heading className={styles.staticHeading}>
          {icon ? <span className="vxm-card-icon">{icon}</span> : null}
          <span className={styles.titleRow}>
            <span className={styles.title}>
              {title}
              {status ? <StatusTag>{status}</StatusTag> : null}
            </span>
            {summary ? <span className={styles.summary}>{summary}</span> : null}
          </span>
        </Heading>
        <div className={styles.panel}>
          {children}
        </div>
      </article>
    );
  }

  return (
    <article
      className={`${styles.card} ${expanded ? styles.cardOpen : ''} ${
        alwaysOpenOnDesktop ? styles.cardDesktopOpen : ''
      }`}
    >
      <Heading className={styles.heading}>
        <button
          type="button"
          id={buttonId}
          className={styles.trigger}
          aria-expanded={expanded}
          aria-controls={panelId}
          tabIndex={pinnedOpen ? -1 : 0}
          onClick={() => {
            if (pinnedOpen) return;
            setOpen((value) => !value);
          }}
        >
          {icon ? <span className="vxm-card-icon">{icon}</span> : null}
          <span className={styles.titleRow}>
            <span className={styles.title}>
              {title}
              {status ? <StatusTag>{status}</StatusTag> : null}
            </span>
            {summary ? <span className={styles.summary}>{summary}</span> : null}
          </span>
          <ChevronIcon className={styles.chevron} />
        </button>
      </Heading>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={styles.panel}
      >
        {children}
      </div>
    </article>
  );
};

export default ExpandableCard;
